import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, Typography, Button, Descriptions, Row, Col, Space, Spin, Tag, message, Tabs } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getCalendarEventById, updateCalendarEvent } from '@/api/calendar';
import { CalendarEvent } from '@/types/community/calendar';
import { useAuth } from '@/contexts/AuthContext';
import CalendarEventModal from '@/components/features/community/CalendarEventModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import CommentsFormSection from '@/components/common/CommentsFormSection';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteCalendarEvent } from '@/api/calendar';
import dayjs from 'dayjs';
import EmployeeTab from '@/components/tab/EmployeeTab';
import ResidentTab from '@/components/tab/ResidentTab';
import VendorTab from '@/components/tab/VendorTab';
import { UserOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const eventTypeColors: Record<CalendarEvent['type'], string> = {
  meeting: 'blue',
  activity: 'green',
  other: 'purple'
};

const CalendarEventDetailsPage: React.FC = () => {
  const { t } = useTranslation(['calendar', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { event: CalendarEvent } | null;
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();

  // State for modals
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<Error | undefined>(undefined);

  // Queries - Always fetch to ensure we have the latest data
  const { data: event, isLoading, refetch } = useQuery({
    queryKey: ['calendar', id],
    queryFn: () => getCalendarEventById(id!, firestoreUser!),
    enabled: !!id && !!firestoreUser,
    staleTime: 0, // Always consider data stale to allow immediate refetch
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Use state data if available and more recent, otherwise use fetched data
  const displayEvent = state?.event && !event ? state.event : event;

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (eventId: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteCalendarEvent(eventId, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('calendar:eventDeleted'));
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      setIsDeleteModalVisible(false);
      setDeleteError(undefined);
      navigate('/community/calendar');
    },
    onError: (error: Error) => {
      setDeleteError(error);
    }
  });

  // Handlers
  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = () => {
    setIsDeleteModalVisible(true);
    setDeleteError(undefined);
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['calendar', id] });
  };

  const handleEditCancel = () => {
    setIsEditModalOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (id) {
      deleteMutation.mutate(id);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setDeleteError(undefined);
  };

  const handleErrorDismiss = () => {
    setDeleteError(undefined);
  };

  // Comment handlers
  const handleAddComment = async (comment: any) => {
    if (!displayEvent || !firestoreUser) return;

    try {
      const newComment = {
        ...comment,
        createdAt: new Date()
      };

      const updatedComments = [...(displayEvent.comments || []), newComment];

      await updateCalendarEvent(displayEvent.id!, { comments: updatedComments }, firestoreUser);
      
      message.success(t('common:createSuccess'));
      
      // Always invalidate and refetch to get the latest data
      queryClient.invalidateQueries({ queryKey: ['calendar', id] });
      queryClient.invalidateQueries({ queryKey: ['calendarEvents'] });
      await refetch();
    } catch (error: any) {
      console.error('Error adding comment:', error);
      message.error(error.message || t('common:createError'));
    }
  };

  const handleUpdateComment = async (commentIndex: number, content: string) => {
    if (!displayEvent || !firestoreUser) return;

    try {
      const updatedComments = [...(displayEvent.comments || [])];
      updatedComments[commentIndex] = {
        ...updatedComments[commentIndex],
        content
      };

      await updateCalendarEvent(displayEvent.id!, { comments: updatedComments }, firestoreUser);
      
      message.success(t('common:updateSuccess'));
      
      // Always invalidate and refetch to get the latest data
      queryClient.invalidateQueries({ queryKey: ['calendar', id] });
      queryClient.invalidateQueries({ queryKey: ['calendarEvents'] });
      await refetch();
    } catch (error: any) {
      console.error('Error updating comment:', error);
      message.error(error.message || t('common:updateError'));
    }
  };

  const handleDeleteComment = async (commentIndex: number) => {
    if (!displayEvent || !firestoreUser) return;

    try {
      const updatedComments = [...(displayEvent.comments || [])];
      updatedComments.splice(commentIndex, 1);

      await updateCalendarEvent(displayEvent.id!, { comments: updatedComments }, firestoreUser);
      
      message.success(t('common:deleteSuccess'));
      
      // Always invalidate and refetch to get the latest data
      queryClient.invalidateQueries({ queryKey: ['calendar', id] });
      queryClient.invalidateQueries({ queryKey: ['calendarEvents'] });
      await refetch();
    } catch (error: any) {
      console.error('Error deleting comment:', error);
      message.error(error.message || t('common:deleteError'));
    }
  };

  // Helper function to get organizer type and ID
  const getOrganizerInfo = () => {
    if (displayEvent?.hostedBy) {
      return { type: displayEvent.hostedBy.type, id: displayEvent.hostedBy.id, name: displayEvent.hostedBy.name };
    }
    return null;
  };

  const organizerInfo = getOrganizerInfo();

  // Loading state - only show if no state data is available
  if (isLoading && !state?.event) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayEvent) {
    return <Card>Event not found</Card>;
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              {t('common:back')}
            </Button>
            <Title level={3}>{displayEvent.title}</Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>
      
      {/* Content with Tabs */}
      <Tabs 
        defaultActiveKey="details" 
        type="card"
        items={[
          {
            key: 'details',
            label: t('calendar:eventDetails'),
            children: (
              <Card>
                <Descriptions bordered column={1}>
                  <Descriptions.Item label={t('calendar:title')}>
                    <Text strong>{displayEvent.title}</Text>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label={t('calendar:description')}>
                    <Paragraph>{displayEvent.description}</Paragraph>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label={t('calendar:type')}>
                    <Tag color={eventTypeColors[displayEvent.type]}>
                      {t(`calendar:types.${displayEvent.type}`)}
                    </Tag>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label={t('calendar:startTime')}>
                    {dayjs(displayEvent.start).format('YYYY-MM-DD HH:mm')}
                  </Descriptions.Item>
                  
                  <Descriptions.Item label={t('calendar:endTime')}>
                    {dayjs(displayEvent.end).format('YYYY-MM-DD HH:mm')}
                  </Descriptions.Item>
                  
                  {/* Organizer */}
                  {displayEvent.hostedBy && (
                    <Descriptions.Item label={t('calendar:organizer')}>
                      <Space>
                        <UserOutlined />
                        <Text strong>{displayEvent.hostedBy.name}</Text>
                        <Tag color="blue">{displayEvent.hostedBy.type}</Tag>
                      </Space>
                    </Descriptions.Item>
                  )}
                  
                  {displayEvent.attendees && displayEvent.attendees.length > 0 && (
                    <Descriptions.Item label={t('calendar:attendees')}>
                      {displayEvent.attendees.map(attendee => attendee.name).join(', ')}
                    </Descriptions.Item>
                  )}
                  
                  {displayEvent.notes && (
                    <Descriptions.Item label={t('calendar:notes')}>
                      <Paragraph>{displayEvent.notes}</Paragraph>
                    </Descriptions.Item>
                  )}
                  
                  {displayEvent.status && (
                    <Descriptions.Item label={t('calendar:status')}>
                      <Tag color={displayEvent.status === 'scheduled' ? 'blue' : displayEvent.status === 'completed' ? 'green' : 'default'}>
                        {t(`calendar:statuses.${displayEvent.status}`)}
                      </Tag>
                    </Descriptions.Item>
                  )}
                </Descriptions>
                
                {/* Comments Section */}
                <div style={{ marginTop: 24 }}>
                  <Typography.Title level={4}>{t('common:comments')}</Typography.Title>
                  <CommentsFormSection
                    comments={displayEvent.comments || []}
                    onAddComment={handleAddComment}
                    onUpdateComment={handleUpdateComment}
                    onDeleteComment={handleDeleteComment}
                    showForm={true}
                    maxHeight="400px"
                  />
                </div>
              </Card>
            )
          },
          ...(organizerInfo ? [{
            key: 'organizer',
            label: t('calendar:organizerDetails'),
            children: (
              <>
                {organizerInfo.type === 'employee' && (
                  <EmployeeTab employeeId={organizerInfo.id} />
                )}
                {organizerInfo.type === 'resident' && (
                  <ResidentTab residentId={organizerInfo.id} />
                )}
                {organizerInfo.type === 'vendor' && (
                  <VendorTab vendorId={organizerInfo.id} />
                )}
              </>
            )
          }] : [])
        ]}
      />

      {/* Edit Modal */}
      <CalendarEventModal
        open={isEditModalOpen}
        event={displayEvent}
        onSuccess={handleEditSuccess}
        onCancel={handleEditCancel}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={isDeleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('calendar:confirmDeleteTitle')}
        error={deleteError}
        onErrorDismiss={handleErrorDismiss}
      />
    </div>
  );
};

export default CalendarEventDetailsPage; 